import React, {useMemo} from 'react';
import { Query, CloudObject } from 'olympe';

import TreeItem from '@mui/lab/TreeItem';
import { Checkbox } from '@mui/material';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import CheckIcon from '@mui/icons-material/Check';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';


import Component from "../../../models/Component";
import InspectionEntity from "../../../models/InspectionEntity";
import System from "../../../models/System";

export const RenderTree = ({ node, checkedComponents, onCheckChange, availablePredicate, showCheckboxes, evalEntities, isInspectMode, checkedEntitiesForEditing }) => {
    const childrenTags = useMemo(() => {
        if (showCheckboxes && !isInspectMode) {

            let inspectionsQuery = Query.from(node.tag);
            const entity = CloudObject.get(node.tag);
            if(entity instanceof System) {
                inspectionsQuery = inspectionsQuery.follow(System.componentsRel)
                .followRecursively(Component.subComponentsRel, true)
                .follow(Component.inspectionsRel).andReturn();
            } else if(entity instanceof Component) {
                inspectionsQuery = inspectionsQuery.followRecursively(Component.subComponentsRel, true)
                .follow(Component.inspectionsRel).andReturn();
            }

            // Filter out the unavailable inspections in case it is defined.
            if (availablePredicate) {
                inspectionsQuery = inspectionsQuery.follow(InspectionEntity.presumedStatesRel).filter(availablePredicate);
            }
            const inspections = inspectionsQuery.executeFromCache().map((inspection) => inspection.getTag());

            const getSubComponents = (n) => n.children?.flatMap((child) => [child.tag].concat(getSubComponents(child))) ?? [];
            const subComponents = getSubComponents(node);

            return subComponents.concat(inspections);
        }
        return [];
    }, [availablePredicate, showCheckboxes, isInspectMode]);

    const someSelectedChildren = childrenTags.length === 0 || childrenTags.some((child) => checkedComponents.has(child));
    const allSelectedChildren = someSelectedChildren && childrenTags.every((child) => checkedComponents.has(child));

    const selected = checkedComponents.has(node.tag);
    const fullySelected = selected && allSelectedChildren;


    const excludeNoObservationNode = node.inspections?.filter((ins) => checkedComponents.has(ins)) ?? [];
    const excludeNoEvaluation = excludeNoObservationNode.filter((ins) => evalEntities?.has(ins));
    const someSelectedChildrenEval = excludeNoObservationNode.some((child) => evalEntities?.has(child));
    const allSelectedChildrenEval = someSelectedChildrenEval && excludeNoObservationNode.every((child) => evalEntities?.has(child));

    const fullySelectedEval = excludeNoEvaluation.length > 0 && allSelectedChildrenEval;

    const someSelectedChildrenDisabled = childrenTags.some((child) => checkedEntitiesForEditing?.has(child));

    return (
        <TreeItem
            key={node.tag}
            nodeId={node.tag}
            label={
                <>
                    {showCheckboxes && (
                        <Checkbox
                            checked={fullySelected}
                            indeterminate={!fullySelected && selected}
                            indeterminateIcon={<IndeterminateCheckBoxIcon sx={{ color: someSelectedChildrenDisabled ? 'grey' : 'orange' }} />}
                            sx={{
                                color: 'pink',
                                '&.Mui-checked': { color: someSelectedChildrenDisabled ? 'grey' : 'mediumaquamarine' },
                            }}
                            onChange={(event) => onCheckChange(event.currentTarget.checked, [node])}
                            onClick={(e) => e.stopPropagation()}
                            disabled={someSelectedChildrenDisabled}
                        />
                    )}
                    <div className={isInspectMode ? 'muiTreeItem-label-justified' : ''}>
                        <div
                            className={!selected || someSelectedChildrenDisabled ? 'tree-item-title-disabled node-title' : 'node-title'}>
                            {node.name}
                        </div>
                        <div>
                            {selected && isInspectMode ? (
                                <>
                                    {fullySelectedEval && <CheckIcon sx={{ color: 'mediumaquamarine' }} />}
                                    {!fullySelectedEval && excludeNoEvaluation.length > 0 && <ChatOutlinedIcon sx={{ color: 'orange' }} />}
                                    {excludeNoEvaluation.length === 0 && <QuestionMarkIcon sx={{ color: '#006DB0' }} />}
                                </>
                            ) : null}
                        </div>
                    </div>
                </>
            }>
            {node.children
                ? node.children.map((childrenNode) => (
                      <RenderTree
                          key={childrenNode.tag}
                          node={childrenNode}
                          checkedComponents={checkedComponents}
                          onCheckChange={onCheckChange}
                          availablePredicate={availablePredicate}
                          showCheckboxes={showCheckboxes}
                          evalEntities={evalEntities}
                          isInspectMode={isInspectMode}
                          checkedEntitiesForEditing={checkedEntitiesForEditing}
                      />
                  ))
                : null}
        </TreeItem>
    );
};
